import Vue from 'vue';
import i18n from '@/i18n';
import store from '@/store';
import { addWeeks } from '@/helpers/DateHelpers';
import * as giftConsts from '@/constants/Gifting';
import GiftingService from '@/services/gifting/GiftingService';
import PostcodeService from '@/services/logistics/PostcodeService';
import { PRODUCT_TYPE_MULTI, PRODUCT_TYPE_SINGLE, PRODUCT_TYPE_BUMPER_SINGLE, SPECIAL_DATES_GIFT_COUPON, PRODUCT_TYPE_POP_SINGLE } from '@/constants/Gifting';

const getDefaultState = () => {
    return {
        sections: {
            [giftConsts.SECTION_CHOOSE_A_GIFT]: false,
            [giftConsts.SECTION_CHOOSE_A_BOX_TYPE]: false,
            [giftConsts.SECTION_NUMBER_OF_DELIVERIES]: false,
            [giftConsts.SECTION_DELIVERY_ADDRESS]: false,
            [giftConsts.SECTION_FREDDIES_ADDONS]: false,
            [giftConsts.SECTION_FREDDIES_SHOP]: false,
            [giftConsts.SECTION_DELIVERY_DATE]: false,
            [giftConsts.SECTION_GIFT_CARD]: false,
        },
        giftDetails: {
            additionalProducts: [],
            additionalProductsCost: 0,
            selectedBoxName: null,
            deliveryType: null,
            subscription: {
                frequency: null,
                numberOfMonths: 1,
                availableDeliveryDay: null,
            },
            addons: {
                paidVase: false,
            },
            contact: {
                mobileNumber: null,
                notifier: 'You',
            },
            hasProcessed: false,
            giftReference: null,
            giftNoteUrl: null,
        },
        prices: {
            haveFetched: false,
            paidVase: null,
            oneOff: null,
            subscription: null,
            oneOffBumper: null,
            popSingle: null,
            popMulti: null,
            subscriptionBumper: null,
        },
        productTypes: null,
        message: {
            to: null,
            body: null,
            from: null,
        },
        logistics: {
            firstDeliveryDateAvailable: null,
            deliveryDateSelected: null,
        },
        coupons: {
            multiGift: null,
            singleGift: null,
            specialDatesCoupons: null,
        },
        incompleteGiftingId: null,
        incompleteGifting: {
            total_steps: null,
            current_step: null,
            data: {
                type: null,
                first_name: null,
                last_name: null,
                gift_note_url: null,
                paid_vase: null,
                frequency: null,
                number_of_months: null,
                address: {
                    address_1: null,
                    address_2: null,
                    city: null,
                    country: null,
                    postcode: null,
                },
                delivery: {
                    delivery_date: null,
                    text_to_me: null,
                    telephone: null,
                },
                gift: {
                    from: null,
                    body: null,
                    to: null,
                },
            },
        },
        holidayBox: {
            showHolidayBoxModal: false,
        },
        orderedBoxName: null,
        products: [
            {
                id: 1,
                boxName: i18n.t('GIFTING_POP_BOX'),
                type: PRODUCT_TYPE_POP_SINGLE,
                description: i18n.t('GIFTING_POP_BOX_TEXT'),
                product_family: 'lite',
                image_url: 'https://freddiesflowers-com.s3.eu-west-2.amazonaws.com/images/gifting/Gifting-Web-Mobile-Pop.jpg',
                enabled: false,
            },
            {
                id: 2,
                boxName: i18n.t('GIFTING_CLASSIC_BOX'),
                type: PRODUCT_TYPE_SINGLE,
                description: i18n.t('GIFTING_CLASSIC_BOX_TEXT'),
                product_family: 'standard',
                image_url: 'https://freddiesflowers-com.s3.eu-west-2.amazonaws.com/images/gifting/Gifting-Web-Mobile-Classic.jpg',
                enabled: true,
            },
            {
                id: 3,
                boxName: i18n.t('GIFTING_SHOWSTOPPER_BOX'),
                type: PRODUCT_TYPE_BUMPER_SINGLE,
                description: i18n.t('GIFTING_SHOWSTOPPER_BOX_TEXT'),
                product_family: 'bumper',
                image_url: 'https://freddiesflowers-com.s3.eu-west-2.amazonaws.com/images/gifting/Gifting-Web-Mobile-SS.jpg',
                enabled: true,
            },
        ],
        giftDeliveryDates: [],
    };
};

let postcodeService = new PostcodeService();
let giftingService = new GiftingService();

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },

        setAddonVase(state, payload) {
            state.giftDetails.addons.paidVase = payload;
        },

        setSelectedBoxName(state, payload) {
            state.giftDetails.selectedBoxName = payload;
        },

        setDeliveryDateSelected(state, payload) {
            state.logistics.deliveryDateSelected = payload;
        },

        setDeliveryType(state, payload) {
            state.giftDetails.deliveryType = payload;
        },

        setFirstDeliveryDateAvailable(state, payload) {
            state.logistics.firstDeliveryDateAvailable = payload;
        },

        setGiftNoteUrl(state, payload) {
            state.giftDetails.giftNoteUrl = payload;
        },

        setGiftProcessed(state, payload) {
            state.giftDetails.hasProcessed = true;
        },

        setGiftReference(state, payload) {
            state.giftDetails.giftReference = payload;
        },

        setMultiGiftCoupon(state, payload) {
            state.coupons.multiGift = payload;
        },

        setNotifier(state, payload) {
            state.giftDetails.contact.notifier = payload;
        },

        setNotifierNumber(state, payload) {
            state.giftDetails.contact.mobileNumber = payload;
        },

        setProductTypes(state, payload) {
            state.productTypes = payload;
        },

        setProductPrices(state, payload) {
            payload.forEach(item => {
                switch (item.name) {
                case 'paidVase':
                    Vue.set(state.prices, 'paidVase', item.price);
                    break;
                case 'freddiesHurricaneVase':
                    Vue.set(state.prices, 'hurricaneVase', item.price);
                    break;
                case 'freddiesOpticVase':
                    Vue.set(state.prices, 'opticVase', item.price);
                    break;
                case 'oneDelivery':
                    Vue.set(state.prices, 'oneOff', item.price);
                    break;
                case 'multipleDeliveries':
                    Vue.set(state.prices, 'subscription', item.price);
                    break;
                case 'bumperMulti':
                    Vue.set(state.prices, 'subscriptionBumper', item.price);
                    break;
                case 'bumperSingle':
                    Vue.set(state.prices, 'oneOffBumper', item.price);
                    break;
                case 'popSingle':
                    Vue.set(state.prices, 'popSingle', item.price);
                    break;
                case 'popMulti':
                    Vue.set(state.prices, 'popMulti', item.price);
                    break;
                }
            });
        },

        setSingleGiftCoupon(state, payload) {
            state.coupons.singleGift = payload;
        },

        setSpecialDatesCoupon(state, payload) {
            state.coupons.specialDatesCoupons = payload;
        },

        setSectionComplete(state, payload) {
            state.sections[payload] = true;
        },

        setSubscriptionFrequency(state, payload) {
            state.giftDetails.subscription.frequency = payload;
        },

        setSubscriptionNumberOfMonths(state, payload) {
            state.giftDetails.subscription.numberOfMonths = payload;
        },

        updateMessageBody(state, payload) {
            state.message.body = payload;
        },

        updateMessageFrom(state, payload) {
            state.message.from = payload;
        },

        updateMessageTo(state, payload) {
            state.message.to = payload;
        },

        updateAvailableDeliveryDay(state, payload) {
            state.giftDetails.subscription.availableDeliveryDay = payload;
        },

        setincompleteGiftingId(state, payload) {
            state.incompleteGiftingId = payload;
        },

        updateTotalSteps(state, payload) {
            state.incompleteGifting.total_steps = payload;
        },

        updateCurrentStep(state, payload) {
            state.incompleteGifting.current_step = payload;
        },

        updateProducts(state, payload) {
            state.giftDetails.additionalProducts = payload;
        },

        updateProductsTotalCost(state) {
            let price = 0;

            state.giftDetails.additionalProducts.forEach(item => {
                price += parseFloat(item.price) * parseFloat(item.quantity);
            });
            state.giftDetails.additionalProductsCost = parseFloat(price.toFixed(2));
        },

        updateGiftingAddress(state, payload) {
            state.incompleteGifting.data.first_name = payload.first_name;
            state.incompleteGifting.data.last_name = payload.last_name;
            state.incompleteGifting.data.address.address_1 = payload.address1;
            state.incompleteGifting.data.address.address_2 = payload.address2;
            state.incompleteGifting.data.address.city = payload.city;
            state.incompleteGifting.data.address.country = payload.country;
            state.incompleteGifting.data.address.postcode = payload.postcode;
        },

        updateIncompleteGiftingData(state, payload) {
            state.incompleteGifting.data.type = state.giftDetails.deliveryType;
            state.incompleteGifting.data.delivery.delivery_date = state.logistics.deliveryDateSelected;
            state.incompleteGifting.data.delivery.text_to_me = state.giftDetails.contact.notifier === 'You';
            state.incompleteGifting.data.delivery.telephone = state.giftDetails.contact.mobileNumber;
            state.incompleteGifting.data.gift.from = state.message.from;
            state.incompleteGifting.data.gift.body = state.message.body;
            state.incompleteGifting.data.gift.to = state.message.to;
            state.incompleteGifting.data.gift_note_url = state.giftDetails.giftNoteUrl;
            state.incompleteGifting.data.paid_vase = state.giftDetails.addons.paidVase;
            state.incompleteGifting.data.frequency = state.giftDetails.subscription.frequency;
            state.incompleteGifting.data.number_of_months = state.giftDetails.subscription.numberOfMonths;
        },
        setShowHolidayBoxModal(state, payload) {
            state.holidayBox.showHolidayBoxModal = payload;
        },
        setOrderedBoxName(state, payload) {
            state.orderedBoxName = payload;
        },
        setGiftDeliveryDates(state, payload) {
            state.giftDeliveryDates = payload;
        },
    },
    actions: {
        async fetchAvailableDeliveryDay({ commit, state, rootGetters }, postcode = null) {
            if (!postcode) {
                let selectedAddress = rootGetters['address/getSelectedGiftingAddress'];

                postcode = selectedAddress.postcode;
            }
            return await new Promise(function(resolve, reject) {
                postcodeService.getAvailableDeliveryDay(postcode)
                    .then(response => {
                        commit('updateAvailableDeliveryDay', response.data.message);
                        store.commit('subscription/setProductTypes', response.data.products);
                    })
                    .catch(error => reject(error));
            });
        },

        async fetchFirstDeliveryDateAvailable({ commit, state, rootGetters }, forceReload = false) {
            if (state.prices.haveFetched === false || forceReload === true) {
                let selectedAddress = rootGetters['address/getSelectedGiftingAddress'];
                let postcode = selectedAddress.postcode;

                return await new Promise(function(resolve, reject) {
                    let date = postcodeService.legacyGetFirstDeliveryDayForPostcode(postcode);

                    if (date !== false) {
                        commit('setFirstDeliveryDateAvailable', date);
                    }

                    reject(date);
                });
            }
        },

        async fetchPrices({ commit, state }, forceReload = false) {
            if (state.prices.haveFetched === false || forceReload === true) {
                state.haveFetched = true;

                return await new Promise(function(resolve, reject) {
                    giftingService.fetchPrices()
                        .then(response => {
                            commit('setProductPrices', response.data.items);
                            commit('setProductTypes', response.data.items);
                            Vue.set(state.prices, 'haveFetched', true);
                        })
                        .catch(error => {
                            Vue.set(state.prices, 'haveFetched', false);
                            reject(error);
                        });
                });
            }
        },

        fetchGiftingCoupons({ commit, rootGetters }) {
            const coupons = rootGetters['account/getCoupons'];
            const multiCoupon = coupons && coupons.multiGift;
            const singleCoupon = coupons && coupons.singleGift;
            const specialDatesCoupon = coupons && coupons[SPECIAL_DATES_GIFT_COUPON];
            commit('setMultiGiftCoupon', multiCoupon);
            commit('setSingleGiftCoupon', singleCoupon);
            commit('setSpecialDatesCoupon', specialDatesCoupon);
        },

        async fetchGiftDeliveryDate({ commit, state, rootGetters }) {
            return await new Promise(function(resolve, reject) {
                giftingService.fetchGiftDeliveryDates()
                    .then(response => {
                        commit('setGiftDeliveryDates', response.data.data);
                    })
                    .catch(error => reject(error));
            });
        },

        resetGifting({ commit }) {
            commit('resetState');
        },
    },
    getters: {
        getAdditionalProducts: (state) => state.giftDetails.additionalProducts,

        getAdditionalProductsTotalCost: (state) => state.giftDetails.additionalProductsCost,

        getDeliveryDateSelected: (state) => state.logistics.deliveryDateSelected,

        getDeliveryType: (state) => state.giftDetails.deliveryType,

        getFrequencyForSubscription: (state) => state.giftDetails.subscription.frequency,

        getGiftMessage: (state) => {
            return {
                to: state.message.to,
                body: state.message.body,
                from: state.message.from,
            };
        },

        getGiftNoteUrl: (state) => state.giftDetails.giftNoteUrl,

        getGiftReference: (state) => state.giftDetails.giftReference,

        getGiftType: (state) => state.giftDetails.deliveryType,

        getNotifier: (state) => state.giftDetails.contact.notifier,

        getNotifierNumber: (state) => state.giftDetails.contact.mobileNumber,

        getMessageBody: (state) => state.message.body,

        getMessageFrom: (state) => state.message.from,

        getMessageTo: (state) => state.message.to,

        getMultiGiftCoupon: (state) => state.coupons.multiGift,

        getNumberOfDeliveriesForSubscription: (state, getters) => {
            if (getters.isSubscription) {
                let months = state.giftDetails.subscription.numberOfMonths;

                switch (getters.getFrequencyForSubscription) {
                case giftConsts.PLAN_WEEKLY:
                    return parseInt(months * (52 / 12));
                case giftConsts.PLAN_FORTNIGHTLY:
                    return parseInt(months * (26 / 12));
                case giftConsts.PLAN_MONTHLY:
                    return parseInt(months);
                default:
                    return 0;
                }
            }
        },

        isSubscription: (state, getters) => {
            return getters.getGiftType === giftConsts.GIFT_TYPE_SUBSCRIPTION;
        },

        getNumberOfMonthsForSubscription: (state) => state.giftDetails.subscription.numberOfMonths,

        getHavePricesFetched: state => state.prices.haveFetched,

        getSelectedVasePrice: (state) => {
            if (state.giftDetails.selectedBoxName === PRODUCT_TYPE_POP_SINGLE) return state.prices.opticVase;
            return state.giftDetails.selectedBoxName === PRODUCT_TYPE_BUMPER_SINGLE ? state.prices.hurricaneVase : state.prices.paidVase;
        },

        getPaidVasePrice: (state) => parseInt(state.prices.paidVase || 0),

        getHurricaneVasePrice: (state) => parseInt(state.prices.hurricaneVase || 0),

        getOpticVasePrice: (state) => parseInt(state.prices.opticVase || 0),

        getPricePerOneOffDelivery: (state) => state.prices.oneOff,

        getPricePerSubscriptionDelivery: (state) => state.prices.subscription,

        getPricePerOneOffBumperDelivery: (state) => state.prices.oneOffBumper,

        getPricePerSubscriptionBumperDelivery: (state) => state.prices.subscriptionBumper,

        getSelectedBoxName: (state) => state.giftDetails.selectedBoxName,

        getPricePerOneOffPop: (state) => state.prices.popSingle,

        getPricePerSubscriptionPop: (state) => state.prices.popMulti,

        getProductTypeIdFromName: (state, getters) => (name) => {
            if (name === null) name = getters.isSubscription ? PRODUCT_TYPE_MULTI : PRODUCT_TYPE_SINGLE;
            return state.productTypes?.filter((item) => item.name === name)[0].id;
        },

        getProductTypePriceFromName: (state, getters) => (name) => {
            if (name === null) name = getters.isSubscription ? PRODUCT_TYPE_MULTI : PRODUCT_TYPE_SINGLE;
            return state.productTypes?.filter((item) => item.name === name)[0]?.price;
        },

        getProductTypePriceFromId: (state) => (id) => {
            return state.productTypes?.filter((item) => item.id === id)[0].price;
        },

        getSingleGiftCoupon: (state) => state.coupons.singleGift,

        getSpecialDatesGiftCoupon: (state) => state.coupons.specialDatesCoupons,

        getIncompleteGifting: (state) => state.incompleteGifting,

        getIncompleteGiftingId: (state) => state.incompleteGiftingId,

        getSubscriptionMaximumDeliveryMonthsAllowed: () => 12,

        getSubscriptionMinimumDeliveryMonthsAllowed: (state, getters) => {
            switch (getters.getFrequencyForSubscription) {
            case giftConsts.PLAN_WEEKLY:
                return 1;
            case giftConsts.PLAN_FORTNIGHTLY:
                return 1;
            case giftConsts.PLAN_MONTHLY:
                return 3;
            default:
                return 1;
            }
        },

        getSubscriptionPredictedLastDeliveryDate: (state, getters) => {
            const frequency = getters.getFrequencyForSubscription;
            const count = getters.getNumberOfDeliveriesForSubscription;
            const deliveryDay = getters.getDeliveryDateSelected;

            let deliveryDate = null;
            let options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

            if (frequency !== null && count !== null && deliveryDay !== null) {
                switch (frequency) {
                case giftConsts.PLAN_WEEKLY:
                    deliveryDate = addWeeks(deliveryDay, count);
                    break;
                case giftConsts.PLAN_FORTNIGHTLY:
                    deliveryDate = addWeeks(deliveryDay, count * 2);
                    break;
                case giftConsts.PLAN_MONTHLY:
                    const deliveriesRemaining = count - 1;
                    const weeks = deliveriesRemaining * 4.33;
                    deliveryDate = addWeeks(deliveryDay, Math.round(weeks));
                    break;
                }
            }

            return !deliveryDate ? null : deliveryDate.toLocaleString(process.env.VUE_APP_DATE_LOCALE, options);
        },

        getTotalPriceForDeliveries: (state, getters) => {
            if (getters.isSubscription) {
                return getters.getNumberOfDeliveriesForSubscription * getters.getProductTypePriceFromName(state.giftDetails.selectedBoxName);
            }

            // If user selected one delivery, return one delivery price.
            return getters.getProductTypePriceFromName(state.giftDetails.selectedBoxName);
        },

        hasProcessed: state => state.giftDetails.hasProcessed,

        getDeliverableDate: (state) => {
            switch (state.giftDetails.subscription.availableDeliveryDay) {
            case 'Sunday':
                return 0;
            case 'Monday':
                return 1;
            case 'Tuesday':
                return 2;
            case 'Wednesday':
                return 3;
            case 'Thursday':
                return 4;
            case 'Friday':
                return 5;
            case 'Saturday':
                return 6;
            }
        },

        isGiftingEnabled: () => !!process.env.VUE_APP_GIFTING_ENABLED,

        isPaidVaseAttached: (state) => state.giftDetails.addons.paidVase === true,

        getShowHolidayBoxModal: (state) => state.holidayBox.showHolidayBoxModal,

        getGiftProducts: (state, getters) => {
            let products = state.products;
            products.map(item => {
                if (item.type === PRODUCT_TYPE_POP_SINGLE) {
                    item.enabled = store.getters['account/getPopSingleGift'];
                }
            });
            return products;
        },

        getOrderedBoxName: (state) => state.orderedBoxName,

        getGiftDeliveryDates: (state) => state.giftDeliveryDates,
    },
};
